import type { ComponentType, SVGProps } from "react";

import { Link } from "react-aria-components";

import { Badge } from "@/components/badge";
import { cn } from "@/utils/cn";

import { Tooltip, TooltipTrigger } from "../../tooltip";

interface NavItemProps {
  badge?: string;
  href?: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  isActive?: boolean;
  isCollapsed: boolean;
  isDisabled?: boolean;
  label: string;
}

export function NavItem({
  badge,
  href,
  icon: Icon,
  isActive,
  isCollapsed,
  isDisabled,
  label,
}: NavItemProps) {
  const isAbsoluteUrl = href && /^http(s)?:\/\//i.test(href);
  const finalHref = href;

  return (
    <li
      className={cn(
        "hover:bg-bg-primary rounded-md",
        isCollapsed ? "self-center" : "w-full",
        isActive && "bg-bg-primary shadow-xs",
      )}
    >
      <TooltipTrigger delay={250}>
        <Link
          className={cn(
            "flex p-1.5 text-sm font-semibold text-text-secondary hover:text-text-primary outline-hidden",
            isCollapsed ? "justify-center" : "gap-md px-lg",
            isActive && "text-fg-brand-primary",
            isDisabled &&
              "opacity-50 cursor-not-allowed pointer-events-none select-none",
          )}
          href={finalHref}
          {...(isAbsoluteUrl && {
            rel: "noopener noreferrer",
            target: "_blank",
          })}
        >
          <Icon
            className={cn(
              "size-5 shrink-0 text-nav-item-icon-fg",
              isActive && "text-fg-brand-primary",
            )}
          />
          {!isCollapsed && (
            <>
              <span className="whitespace-nowrap">{label}</span>
              {!!badge && (
                <Badge isRounded size="small" variant="brand">
                  {badge}
                </Badge>
              )}
            </>
          )}
        </Link>
        {isCollapsed && <Tooltip placement="right">{label}</Tooltip>}
      </TooltipTrigger>
    </li>
  );
}
